import * as React from 'react';
import { AppState, useAppStateContext } from 'src/appState/AppState';
import { EventRowUnifiedWrapper } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowUnified';
import { EventGroupHeader } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventGroupHeader';
import {
    getAmericanLeagueRowFlags,
    renderAmericanLeagueEventRowIfApplicable,
} from 'src/domains/sportsbook/webview/components/eventsList/americanSportEventRow';
import { EventListWrapper } from 'src/domains/sportsbook/webview/components/eventsList/EventList.style';
import { observer } from 'src/utils/mobx-react';
import {
    EventsGroupViewCompetition,
    EventsGroupViewSport,
} from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupView';
import { EventsGroupState } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupState';
import { UnifiedSportEventsListState } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedSportEventsListState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { SpecialSportsState } from 'src/domains/sportsbook/state/specialSportsState/SpecialSportsState';
import { CompetitionWrapper } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowMobx.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { Collapse } from 'src/ui/components/collapse/sports/02/Collapse';
import { TranslationsStore } from 'src/domains/layouts/state/translationsStore/TranslationsStore';

const DEFAULT_OPEN_GROUPS = 5;

const calculateSportSectionExpand = (
    showHeaderSport: boolean,
    sportGroupVisible: boolean
): { showHeader: boolean; showBody: boolean } => {
    if (showHeaderSport === false) {
        return {
            showHeader: false,
            showBody: true,
        };
    }

    return {
        showHeader: true,
        showBody: sportGroupVisible,
    };
};

const renderCompetition = (
    appState: AppState,
    sportId: string,
    competitionGroup: EventsGroupViewCompetition
): Array<React.ReactNode> => {
    const events = competitionGroup.events;
    const out = [];
    const flags = getAmericanLeagueRowFlags(competitionGroup.name, competitionGroup.sport);

    if (events.length > 0) {
        for (const event of events) {
            const americanRow = renderAmericanLeagueEventRowIfApplicable(event, flags, 'event_unified');
            if (americanRow !== null) {
                out.push(americanRow);
            } else {
                out.push(
                    <EventRowUnifiedWrapper
                        key={`event_unified_${event.id}`}
                        smallEvent={event}
                        sportId={sportId}
                        selectionsHeaders={competitionGroup.possibleMarketAndSelectionInListHeaders}
                        competitionId={appState.appSportsBookState.models.id.getCompetitionId(competitionGroup.id)}
                    />
                );
            }
        }
    }

    return out;
};

const renderSportGroup = (
    appState: AppState,
    sportGroup: EventsGroupViewSport,
    eventsListState: UnifiedSportEventsListState,
    sportsbookVariant: DefaultComponentVariantType | null,
    preferCompetitionSelectionsHeadersOnly?: boolean
): Array<React.ReactNode> => {
    const out = [];
    const tabLastSelectionsHeader = eventsListState.lastSelectionsHeader;
    const rowSportId = sportGroup.id;

    for (const competition of sportGroup.competitions) {
        const competitionName = competition.name;
        const isVisible = competition.visible;

        const selectionsHeader =
            preferCompetitionSelectionsHeadersOnly === true
                ? competition.lastSelectionsHeader
                : tabLastSelectionsHeader.length > 0
                  ? tabLastSelectionsHeader
                  : competition.lastSelectionsHeader;

        out.push(
            <EventGroupHeader
                competitionId={appState.appSportsBookState.models.id.getCompetitionId(competition.id)}
                dataTest='competition-header-unified'
                key={`group_unified_${competition.id}`}
                label={competitionName}
                onToggle={competition.toggle}
                open={isVisible}
                selections={selectionsHeader}
            />
        );

        if (isVisible) {
            const outCompetition =
                sportsbookVariant === DefaultComponentVariantType.SECONDARY ? (
                    <CompetitionWrapper>{renderCompetition(appState, rowSportId, competition)}</CompetitionWrapper>
                ) : (
                    renderCompetition(appState, rowSportId, competition)
                );
            out.push(outCompetition);
        }
    }

    return out;
};

class State {
    public readonly appState: AppState;
    public readonly groupEvents: MobxMapAutoNew<EventsCollectionQueryModel | null, EventsGroupState>;

    public constructor(appState: AppState, specialSportsState: SpecialSportsState | null, allGroupsOpen: boolean) {
        this.appState = appState;

        this.groupEvents = new MobxMapAutoNew((eventsCollection) => {
            const { languagesState } = this.appState;

            return new EventsGroupState(
                allGroupsOpen === true ? 'all-open' : DEFAULT_OPEN_GROUPS,
                languagesState.getTranslation,
                eventsCollection,
                specialSportsState
            );
        });
    }
}

interface EventsListUnifiedPropsType {
    allGroupsOpen?: boolean;
    eventsListState: UnifiedSportEventsListState;
    sportId: string;
    showHeaderSport?: boolean;
    eventsCollectionQueryOverride?: EventsCollectionQueryModel | null;
    groupingSpecialSports?: SpecialSportsState | null;
    preferCompetitionSelectionsHeadersOnly?: boolean;
}

const EventsListUnifiedContent = observer('EventsListUnifiedContent', (props: EventsListUnifiedPropsType) => {
    const appState = useAppStateContext();
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const { sportsbookVariant } = configState.componentVariants;

    const { appSportsBookState } = appState;
    const resolvedSpecialSports =
        props.groupingSpecialSports !== undefined
            ? props.groupingSpecialSports
            : appSportsBookState.specialSportsMap.get(props.sportId);

    const [state] = React.useState(() => new State(appState, resolvedSpecialSports, props.allGroupsOpen === true));

    const eventsQuery = props.eventsCollectionQueryOverride ?? props.eventsListState.events;
    const listToView = state.groupEvents.get(eventsQuery).listToView;

    const translationsStore = TranslationsStore.get(common);

    return (
        <EventListWrapper variant={sportsbookVariant}>
            {listToView.sports.map((sportGroup) => {
                const sportKey = `sport_${sportGroup.id}`;
                const expand = calculateSportSectionExpand(props.showHeaderSport === true, sportGroup.visible);

                const competitionNodes = renderSportGroup(
                    appState,
                    sportGroup,
                    props.eventsListState,
                    sportsbookVariant,
                    props.preferCompetitionSelectionsHeadersOnly
                );

                if (props.showHeaderSport !== true) {
                    return <React.Fragment key={sportKey}>{competitionNodes}</React.Fragment>;
                }

                const sportName = translationsStore.translateSport(sportGroup.id);

                return (
                    <React.Fragment key={sportKey}>
                        {expand.showHeader === true ? (
                            <Collapse
                                dataTest='header-sportLevel'
                                handleToggle={sportGroup.toggle}
                                isOpen={expand.showBody}
                                title={sportName}
                                sportId={sportGroup.id}
                            />
                        ) : null}
                        {expand.showBody === true ? competitionNodes : null}
                    </React.Fragment>
                );
            })}
        </EventListWrapper>
    );
});

export const EventsListUnified = observer('EventsListUnified', (props: EventsListUnifiedPropsType) => {
    const filters_coupon = props.eventsListState.filter_coupon;
    const contentKey =
        props.eventsCollectionQueryOverride !== undefined ? `unified-mixed-${filters_coupon}` : filters_coupon;

    return (
        <EventsListUnifiedContent
            key={contentKey}
            allGroupsOpen={props.allGroupsOpen}
            eventsCollectionQueryOverride={props.eventsCollectionQueryOverride}
            eventsListState={props.eventsListState}
            groupingSpecialSports={props.groupingSpecialSports}
            preferCompetitionSelectionsHeadersOnly={props.preferCompetitionSelectionsHeadersOnly}
            showHeaderSport={props.showHeaderSport}
            sportId={props.sportId}
        />
    );
});
